import React, { useMemo, useState } from "react";
import { Button } from "antd";
import { useTranslation } from "react-i18next";
import {
  TimedActionDraft,
  ControlOption,
} from "../../../components/scheduledActions/scheduledActionTypes";
import ScheduledActionList from "../../../components/scheduledActions/ScheduledActionList";
import TimedActionEditModal from "../../../components/scheduledActions/TimedActionEditModal";
import { useTimedActionsCrud } from "../../../components/scheduledActions/useTimedActionsCrud";
import {
  useControlOptions,
  TIMEZONE_DEFAULT,
} from "../../../components/scheduledActions/scheduledActionDefaults";
import flattenLiftNames from "../../../components/scheduledActions/scheduledActionNames";
import { useServerBankTargets } from "../../../components/scheduledActions/useBankFloorOpenings";
import { useWizard } from "../WizardContext";
import { WizardOpGate, useCurrentWizardStepAccess } from "../WizardStepGate";

interface WizardScheduledActionsProps {
  configuredControlOptions?: ControlOption[];
}

function WizardScheduledActions({
  configuredControlOptions = undefined,
}: WizardScheduledActionsProps): React.ReactElement {
  const [t] = useTranslation("global");
  const { state: wizardState } = useWizard();
  const access = useCurrentWizardStepAccess();
  const { organizationId, serverId, bankIds, bankNames, liftIdsByBank, liftNamesByBank } =
    wizardState;

  const {
    actions,
    isFetching,
    fetchError,
    submitting,
    modalOpen,
    editDraft,
    closeModal,
    handleCreate,
    handleEdit,
    handleSubmit,
    handleDelete,
    handleToggleEnabled,
    alerts,
  } = useTimedActionsCrud({ kind: "organization", organizationId: organizationId ?? "" });

  // Bank selection is wizard-local: it seeds new actions and follows the action
  // being edited. Kept here (not in the hook) because it is unique wizard chrome.
  const [selectedBankId, setSelectedBankId] = useState<string>(bankIds[0] ?? "");

  const controlOptions = useControlOptions(configuredControlOptions);

  const bankOptions = useMemo(
    () => bankIds.map((id) => ({ id, name: bankNames[id] ?? id })),
    [bankIds, bankNames],
  );

  /**
   * Every bank's lifts, keyed by bank — NOT just the wizard's currently selected
   * bank's.
   *
   * The editor targets several banks at once, so scoping this to one bank made
   * the other selected banks' cars unreachable: you could add a second bank to
   * an action and then find no way to name a car inside it. The form narrows
   * this map to the banks actually selected, which is a decision only it can
   * make, because only it knows the live selection.
   */
  const liftsByBank = useMemo(() => {
    const map: Record<string, { id: string; name: string }[]> = {};
    bankIds.forEach((bankId) => {
      const nameMap = liftNamesByBank[bankId] ?? {};
      map[bankId] = (liftIdsByBank[bankId] ?? []).map((id) => ({ id, name: nameMap[id] ?? id }));
    });

    return map;
  }, [bankIds, liftIdsByBank, liftNamesByBank]);

  /**
   * Every wizard bank's floors and openings, from the SERVER's bank list.
   *
   * Deliberately not one `GET /banks/{id}` per bank. That handler authorises
   * against the bank's resolved org and additionally runs
   * `enforceBankScope(bank, PermissionActionView)`, while the list handler
   * authorises with `uuid.Nil` and applies no per-bank gate — so a bank the
   * operator can see on every other wizard step could still be refused here, and
   * the floor grid would report that it could not load the floors. The list
   * preloads the same `BankFloors` and `Lifts.LiftFloors`, and it is the endpoint
   * the Lifts & Floors step already uses, which is why that screen shows floors
   * for banks this panel could not read.
   *
   * Covers ALL of the server's banks, not just the selected one: the editor
   * targets several banks at once and narrows the map itself from the live
   * selection.
   */
  const {
    floorsByBank,
    liftDoorsByBank,
    isLoading: floorsLoading,
    failures: floorLoadFailures,
  } = useServerBankTargets(serverId ?? "", bankIds);

  const selectors = useMemo(
    () => ({
      bankOptions,
      liftsByBank,
      floorsByBank,
      liftDoorsByBank,
      floorsLoading,
      floorLoadFailures,
    }),
    [bankOptions, liftsByBank, floorsByBank, liftDoorsByBank, floorsLoading, floorLoadFailures],
  );

  const flatLiftNames = useMemo(() => flattenLiftNames(liftNamesByBank), [liftNamesByBank]);

  const onCreate = () => handleCreate({ bank_id: selectedBankId || (bankIds[0] ?? "") });

  const onEdit = (action: TimedActionDraft) => {
    if (action.bank_id) setSelectedBankId(action.bank_id);
    handleEdit(action);
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <WizardOpGate op="create">
          <Button type="primary" onClick={onCreate} disabled={!organizationId}>
            {t("scheduledActions.addAction", "Add Scheduled Action")}
          </Button>
        </WizardOpGate>
      </div>

      <ScheduledActionList
        actions={actions}
        loading={isFetching}
        error={fetchError}
        // The shared list hides a control whose handler is left out.
        onEdit={access.update ? onEdit : undefined}
        onDelete={access.delete ? handleDelete : undefined}
        onToggleEnabled={access.update ? handleToggleEnabled : undefined}
        bankNames={bankNames}
        liftNames={flatLiftNames}
        controlOptions={controlOptions}
      />

      <TimedActionEditModal
        editDraft={editDraft}
        open={modalOpen}
        controlOptions={controlOptions}
        timezoneDefault={TIMEZONE_DEFAULT}
        submitting={submitting}
        onSubmit={handleSubmit}
        onCancel={closeModal}
        selectors={selectors}
      />

      {alerts}
    </div>
  );
}

export default WizardScheduledActions;
